import React, {useContext, useEffect, useState} from 'react';
import {DispatchContext} from '../../../../store';
import {useNavigate} from 'react-router';
import {useLocation} from 'react-router-dom';
import {SNACKBAR_OPEN} from '../../../../reducers';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {Form, Formik} from 'formik';
import {Button, Card, Grid} from '@mui/material';
import {conservationAction} from '../../../../services/ConservationServices';
import {conservationInitialValue} from './ConservationModel';
import EntryOneConservation from './EntryOneConservation';
import EntryTwoConservation from './EntryTwoConservation';
import EntryThreeConservation from './EntryThreeConservation';
import BeforeConservationPhoto from '../../../../common/ConservationImages/BeforeConservationPhoto';
import AfterConservationPhoto from '../../../../common/ConservationImages/AfterConservationPhoto';

export default function ConservationUpdate() {
  const dispatch = useContext(DispatchContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get('id');
  const afterId = params.get('id');
  const [conservation, setConservation] = useState(conservationInitialValue);
  const [beforeImgIds, setBeforeImgIds] = useState([]);
  const [afterImgIds, setAfterImgIds] = useState([]);
  const code = 'en';

  useEffect(() => {
    fetchConservationDetail();
  }, []);

  const fetchConservationDetail = () => {
    conservationAction('get', id).then(res => {
      setConservation({
        ...res?.data,
        completion_date: 'mm/dd/yyyy'
      });
    })
  }

  const onSubmit = (values) => {

    const combinedAttachments = (values?.before_photos || []).concat(beforeImgIds || []);

    const uniqueIdsSet = new Set();

    combinedAttachments.forEach(id => {
      const parsedId = parseInt(id);
      const parsedIdFromObj = parseInt(id?.id);

      if (!isNaN(parsedId)) {
        uniqueIdsSet.add(parsedId);
      }

      if (!isNaN(parsedIdFromObj)) {
        uniqueIdsSet.add(parsedIdFromObj);
      }
    });

    const newAttachmentIds = Array.from(uniqueIdsSet);

    const conserveValues = {
      museum_id: values?.museum_id,
      accession_no: values?.accession_no,
      incoming_date: values?.incoming_date,
      completion_date: values?.completion_date,
      before_photo_ids: newAttachmentIds,
      translations: {
        en: {
          condition_assessment: values?.condition_assessment || '',
          treatment_plan: values?.treatment_plan || ''
        }
      },
    }

    const conserveAfterValues = {
      museum_id: values?.museum_id,
      accession_no: values?.accession_no,
      incoming_date: values?.incoming_date,
      completion_date: values?.completion_date,
      before_photo_ids: newAttachmentIds,
      after_photo_ids: afterImgIds?.map((val) => val),
      translations: {
        en: {
          condition_assessment_after: values?.condition_assessment_after || '',
          condition_assessment: values?.condition_assessment || '',
          treatment_plan: values?.treatment_plan || ''
        }
      },
    }

    if (conservation?.state !== 'approved') {
      conservationAction('put', id, {...conserveValues}).then(res => {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'success', message: 'Conservation updated successfully'}
        });
        navigate(`/management/conservators/${id}`)
      })
    } else {
      conservationAction('put', id, {...conserveAfterValues}).then(res => {
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'success', message: 'Conservation updated successfully'}
        });
        navigate(`/management/conservators/${id}`)
      })
    }
  }

  return (
    <div>
      <Box sx={{display: {sm: 'flex', xs: 'column'}, alignItems: 'center', mt: 7, mb: 2}}>
        <Typography className={'text-500 font30'} sx={{color: '#757575'}}>Update Conservation</Typography>
      </Box>
      <Box className={'header-divider'}/>
      <Formik initialValues={conservation}
              onSubmit={onSubmit}
              validateOnBlur={true} enableReinitialize
      >
        {({isSubmitting, touched, errors, values, setFieldValue, ...formik}) => (
          <Form>
            <Box>
              <Grid container spacing={2}>
                <Grid item sm={4} xs={12}>
                  <EntryOneConservation code={code}/>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <EntryTwoConservation code={code}/>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <EntryThreeConservation code={code} conservation={conservation}/>
                </Grid>
              </Grid>
              <Box className={'header-divider'} sx={{marginTop: 10, marginBottom: 10}}/>
              <Grid container spacing={2}>
                {conservation?.state !== 'approved' ?
                  <Grid item sm={4} xs={12}>
                    <Card className={'box-shadow'} sx={{p: 4}}>
                      <Typography><span style={{color: 'red'}}>*</span> Before Photo</Typography>
                      <BeforeConservationPhoto setImageId={setBeforeImgIds} imgId={id}
                                               conservationImgId={beforeImgIds}/>
                    </Card>
                  </Grid> :
                  <Grid item sm={4} xs={12}>
                    <Card className={'box-shadow'} sx={{p: 4}}>
                      <Typography>Before Photo</Typography>
                      <Box style={{
                        border: '1px dashed #929191',
                        width: '100%',
                        height: 100,
                        objectFit: 'contain',
                        borderRadius: 5
                      }}>
                        <Grid container>
                          {conservation?.before_photos?.map((image, index) => (
                            <Grid key={index} item sm={2.4}>
                              <img draggable={false} width={'80%'} height={55}
                                   style={{objectFit: 'cover'}}
                                   src={image?.file}
                                   alt="museum_photo"/>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Card>
                  </Grid>}
                {conservation?.state === 'approved' &&
                <Grid item sm={4} xs={12}>
                  <Card className={'box-shadow'} sx={{p: 4}}>
                    <Typography><span style={{color: 'red'}}>*</span> After Photo</Typography>
                    <AfterConservationPhoto setImageIds={setAfterImgIds} imgId={afterId} afterImgIds={afterImgIds}/>
                  </Card>
                </Grid>
                }
              </Grid>
            </Box>
            <Box>
              <Grid container spacing={2}>
                <Grid item sm={9} xs={12}/>
                <Grid item sm={3} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                      <Button onClick={() => navigate(-1)} variant={'outlined'} color={'error'}
                              fullWidth>Cancel</Button>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Button variant={'contained'} type={'submit'} fullWidth>Submit</Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>
    </div>
  );
}